import React from "react";
import { Stack, useToast } from "@chakra-ui/react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import {
  getNewSessionToken,
  getPlaceDetails,
  getTextSearchPredictions,
} from "@/service/google-maps.service";
import { HomeownerSearchSection } from "./HomeownerSearchSection";
import { RequestAddressSection } from "./RequestAddressSection";
import { RequestDetailsSection } from "./RequestDetailsSection";
import { SelectedHomeownerSummary } from "./SelectedHomeownerSummary";
import {
  ADMIN_CREATE_HOMEOWNER_REQUEST,
  GET_HOMEOWNER_FOR_REQUEST,
  LIST_HOMEOWNER_CLIENTS_FOR_REQUEST,
} from "../graphql";
import { AddressMode, Category, Homeowner, NewAddressInput } from "../types";
import {
  hasRequestCreateValidationErrors,
  RequestCreateValidationErrors,
  validateRequestCreate,
} from "../validation";

type RelayResponse<T> = {
  pageInfo: {
    hasNextPage: boolean;
    endCursor?: string | null;
  };
  edges: Array<{
    cursor: string;
    node: T;
  }>;
};

type HomeownerRequestFormProps = {
  categories: Category[];
};

const blankNewAddress: NewAddressInput = {
  address: "",
  formattedAddress: "",
  city: "",
  state: "",
};

const toDateTimeInput = (date: string) => `${date}T00:00:00.000Z`;

export const HomeownerRequestForm = ({ categories }: HomeownerRequestFormProps) => {
  const history = useHistory();
  const toast = useToast();
  const [search, setSearch] = React.useState("");
  const [selectedHomeowner, setSelectedHomeowner] = React.useState<Homeowner | null>(null);
  const [addressMode, setAddressMode] = React.useState<AddressMode>("saved");
  const [selectedAddressId, setSelectedAddressId] = React.useState("");
  const [newAddress, setNewAddress] = React.useState<NewAddressInput>(blankNewAddress);
  const [addressSearch, setAddressSearch] = React.useState("");
  const [addressPredictions, setAddressPredictions] = React.useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [addressSearchLoading, setAddressSearchLoading] = React.useState(false);
  const [sessionToken, setSessionToken] =
    React.useState<google.maps.places.AutocompleteSessionToken | null>(null);
  const [categoryId, setCategoryId] = React.useState("");
  const [otherCategoryText, setOtherCategoryText] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const [searchHomeowners, searchHomeownersResult] = useLazyQuery<{
    listHomeowners: RelayResponse<Homeowner>;
  }>(LIST_HOMEOWNER_CLIENTS_FOR_REQUEST, {
    fetchPolicy: "network-only",
  });

  const [getHomeowner, homeownerResult] = useLazyQuery<{ homeowner: Homeowner }>(
    GET_HOMEOWNER_FOR_REQUEST,
    {
      fetchPolicy: "network-only",
      onCompleted: ({ homeowner }) => {
        setSelectedHomeowner(homeowner);
        setSelectedAddressId(homeowner.addresses?.[0]?.id || "");
        setAddressMode(homeowner.addresses?.length ? "saved" : "new");
      },
    }
  );

  const [createHomeownerRequest, createHomeownerRequestResult] = useMutation(
    ADMIN_CREATE_HOMEOWNER_REQUEST,
    {
      onCompleted: (data) => {
        const orderCode = data?.adminCreateHomeownerRequest?.order?.orderCode;
        history.push(orderCode ? `/orders/${orderCode}` : "/orders/all");
      },
      onError: (error) => {
        toast({
          title: "Request could not be created",
          description: error.message,
          status: "error",
        });
      },
    }
  );

  const homeowners =
    searchHomeownersResult.data?.listHomeowners.edges.map((edge) => edge.node) || [];
  const visibleHomeowners = search.trim().length >= 2 ? homeowners : [];
  const googlePlacesAvailable = Boolean(window.google?.maps?.places);

  const validationErrors = React.useMemo(
    () =>
      validateRequestCreate({
        clientProfileId: selectedHomeowner?.clientProfile?.id,
        addressMode,
        selectedAddressId,
        newAddress,
        categoryId,
        otherCategoryText,
        startDate,
      }),
    [
      addressMode,
      categoryId,
      newAddress,
      otherCategoryText,
      selectedAddressId,
      selectedHomeowner?.clientProfile?.id,
      startDate,
    ]
  );
  const visibleErrors: RequestCreateValidationErrors = submitAttempted
    ? validationErrors
    : {};

  React.useEffect(() => {
    if (googlePlacesAvailable && !sessionToken) {
      setSessionToken(getNewSessionToken());
    }
  }, [googlePlacesAvailable, sessionToken]);

  React.useEffect(() => {
    const searchTerm = search.trim();

    if (selectedHomeowner || searchTerm.length < 2) {
      return undefined;
    }

    const debounceId = window.setTimeout(() => {
      searchHomeowners({
        variables: {
          first: 10,
          after: null,
          search: searchTerm,
        },
      });
    }, 350);

    return () => window.clearTimeout(debounceId);
  }, [search, searchHomeowners, selectedHomeowner]);

  const handleAddressSearch = async (value: string) => {
    setAddressSearch(value);

    if (!googlePlacesAvailable || value.trim().length < 3) {
      setAddressPredictions([]);
      return;
    }

    const token = sessionToken || getNewSessionToken();
    setSessionToken(token);
    setAddressSearchLoading(true);

    try {
      const predictions = await getTextSearchPredictions(value, token);
      setAddressPredictions(predictions);
    } catch {
      setAddressPredictions([]);
    } finally {
      setAddressSearchLoading(false);
    }
  };

  const handleAddressPredictionClick = async (
    prediction: google.maps.places.AutocompletePrediction
  ) => {
    if (!sessionToken) {
      return;
    }

    const place = await getPlaceDetails(prediction.place_id, sessionToken);
    const state = place.address_components?.find((component) =>
      component.types.includes("administrative_area_level_1")
    );
    const city = place.address_components?.find((component) =>
      component.types.includes("administrative_area_level_2")
    );
    const formattedAddress = place.formatted_address || prediction.description;

    setNewAddress({
      address: place.name || formattedAddress,
      formattedAddress,
      city: city?.long_name || "",
      state: state?.long_name || "",
      coordinates: place.geometry?.location
        ? [place.geometry.location.lng(), place.geometry.location.lat()]
        : undefined,
      placeId: place.place_id,
    });
    setAddressSearch(formattedAddress);
    setAddressPredictions([]);
    setSessionToken(getNewSessionToken());
  };

  const handleSubmit = () => {
    setSubmitAttempted(true);

    if (hasRequestCreateValidationErrors(validationErrors)) {
      toast({
        title: "Review the highlighted fields",
        status: "warning",
      });
      return;
    }

    if (!selectedHomeowner?.clientProfile?.id) {
      return;
    }

    createHomeownerRequest({
      variables: {
        data: {
          clientProfileId: selectedHomeowner.clientProfile.id,
          ...(addressMode === "saved"
            ? { addressId: selectedAddressId }
            : { address: newAddress }),
          ...(otherCategoryText.trim()
            ? { otherCategoryText: otherCategoryText.trim() }
            : { categoryId }),
          ...(description.trim() ? { description: description.trim() } : {}),
          startDate: toDateTimeInput(startDate),
        },
      },
    });
  };

  const handleChangeHomeowner = () => {
    setSelectedHomeowner(null);
    setSelectedAddressId("");
    setAddressMode("saved");
    setSearch("");
  };

  return (
    <Stack spacing={5}>
      <SelectedHomeownerSummary
        homeowner={selectedHomeowner}
        onChangeHomeowner={handleChangeHomeowner}
      />
      {!selectedHomeowner && (
        <HomeownerSearchSection
          search={search}
          homeowners={visibleHomeowners}
          isLoading={searchHomeownersResult.loading}
          error={visibleErrors.homeowner}
          onSearchChange={setSearch}
          onSelectHomeowner={(homeowner) =>
            getHomeowner({ variables: { id: homeowner.id } })
          }
        />
      )}
      <RequestAddressSection
        homeowner={selectedHomeowner}
        isLoading={homeownerResult.loading}
        addressMode={addressMode}
        selectedAddressId={selectedAddressId}
        newAddress={newAddress}
        addressSearch={addressSearch}
        addressPredictions={addressPredictions}
        addressSearchLoading={addressSearchLoading}
        googlePlacesAvailable={googlePlacesAvailable}
        errors={{
          address: visibleErrors.address,
          newAddress: visibleErrors.newAddress,
        }}
        onAddressModeChange={setAddressMode}
        onSavedAddressChange={setSelectedAddressId}
        onNewAddressChange={(patch) =>
          setNewAddress((current) => ({
            ...current,
            ...patch,
          }))
        }
        onAddressSearch={handleAddressSearch}
        onAddressPredictionClick={handleAddressPredictionClick}
      />
      <RequestDetailsSection
        categories={categories}
        categoryId={categoryId}
        otherCategoryText={otherCategoryText}
        description={description}
        startDate={startDate}
        isSubmitting={createHomeownerRequestResult.loading}
        errors={{
          category: visibleErrors.category,
          startDate: visibleErrors.startDate,
        }}
        onCategoryChange={(value) => {
          setCategoryId(value);
          setOtherCategoryText("");
        }}
        onOtherCategoryTextChange={(value) => {
          setOtherCategoryText(value);
          setCategoryId("");
        }}
        onDescriptionChange={setDescription}
        onStartDateChange={setStartDate}
        onSubmit={handleSubmit}
      />
    </Stack>
  );
};
